<template>
  <div class="auth-portal-container">
    <div class="header">
      <div class="header-container">
        <div class="logo-container">
          <img v-if="company === 'cuprum'" src="@/assets/logo-cuprum.png" width="146" alt />
          <img v-else src="@/assets/logo-principal.png" width="146" alt="principal logo" />
        </div>
        <div class="contact-container">
          <div v-if="company === 'cuprum'">
            <img class="phone-icon" src="@/assets/icons/phone.png" alt />
            <div class="pds-typography-bold phone-contact">600 228 7786</div>
            <img class="phone-icon" src="@/assets/icons/shake_phone.png" alt="icono teléfono" />
            <div class="pds-typography-bold phone-contact">22 347 0210</div>
          </div>
          <div v-else>
            <img class="phone-icon" src="@/assets/icons/phone.png" alt />
            <div class="pds-typography-bold phone-contact">800 20 10 02</div>
          </div>
        </div>
      </div>
      <div class="division-line-blue"></div>
    </div>
    <div class="componentContainer">
      <ValidationCode
        v-if="showComponentValidationCode()"
        :company="company"
        :dummyMode="dummyMode"
        :isPartialPortal="isPartialPortal"
        :validationCodeWithError="validationCodeWithError"
        @validationCodeResponse="clientDataValidator"
      />
      <MethodSelector
        v-if="showComponentMethodSelector()"
        :validations="validations"
        :isPartialPortal="isPartialPortal"
        :validationPurpose="validationPurpose"
        @MethodSelectorResponse="beginAuthentication"
      />
      <div v-if="showAuthWidget()" class="auth-widget-container">
        <AuthWidget
          :key="environment + (authMethod || 'all')"
          :kongModeJWT="kongModeJWT"
          :kong-token="jwtToken"
          :rut="clientRut"
          @auth="checkAuthResponse"
          @goBack="returnMethodSelector"
          :dummy-mode="dummyMode"
          :company="company"
          :method="authMethod"
          :environment="environment"
          :provider="provider"
          :light-mode="true"
          :portal-id="portalId"
          :autostart="true"
        />
      </div>
      <ResultScreen
        v-if="showComponentResultScreen()"
        :result="successResult"
        :partialResult="partialResult"
        @continue="checkTries"
      />
      <div v-if="showComponentLoading()" class="loader">
        <vue-feather type="loader" size="64" animation="spin" stroke="#4a4a4a"></vue-feather>
        <p>Estamos validando tus datos...</p>
      </div>
    </div>

    <div class="footer">
      <div class="division-line-orange" />
      <div class="footer-container" v-if="company === 'cuprum'">
        <div class="logo-sp">
          <img src="@/assets/logo-sp.png" width="116" height="52" alt />
        </div>
        <div class="footer-text">
          Infórmese sobre la calidad de servicio de las AFP en la herramienta Infórmate y Decíde, disponible en la
          página web de la Superintendencia de Pensiones. Entrega tu opinión a la SUPERINTENDENCIA DE PENSIONES sobre la
          calidad del servicio de tu AFP.
        </div>
      </div>
      <div class="footer-container" v-else>
        <div class="principal-copyright-text">
          Copyright © 2021, Principal Financial Group - Todos los derechos reservados
        </div>
        <div class="principal-address-text">
          Apoquindo 3600 Piso 10, Las Condes, Santiago, Chile
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ValidationCode from "@/components/authPortal/ValidationCode";
import MethodSelector from "@/components/authPortal/MethodSelector";
import AuthWidget from "@/components/AuthWidget";
import Config, { PRODUCTION } from "@/utils/config";
import ResultScreen from "@/components/authPortal/ResultScreen";
import VueFeather from "vue-feather";
import {
  DummyTraceService,
  TraceService,
  PortalService,
  DummyPortalService,
  KongService
} from "fulfillment-commons-js";

const steps = {
  VALIDATION_CODE: "VALIDATION_CODE",
  METHOD_SELECTOR: "METHOD_SELECTOR",
  AUTHENTICATION_PROCESS: "AUTHENTICATION_PROCESS",
  RESULT_SCREEN: "RESULT_SCREEN",
  LOADING: "LOADING"
};

export default {
  name: "AuthPortal",
  components: {
    ValidationCode,
    MethodSelector,
    AuthWidget,
    ResultScreen,
    VueFeather
  },
  data() {
    // vue-router runs in hash mode here (see auth-portal-main.js), so it only parses
    // query params placed AFTER the "#" (e.g. "#/?company=principal"). Links that place
    // them BEFORE the hash (e.g. "...auth-portal.html?company=principal...#/", which is
    // what gets generated today) leave $route.query empty. Fall back to parsing
    // window.location.search directly so both URL shapes resolve correctly.
    const routeQuery = this.$route.query || {};
    const searchParams = new URLSearchParams(window.location.search);
    const queryParam = name => (name in routeQuery ? routeQuery[name] : searchParams.get(name));
    // dummyMode bypasses real identity-validation services, so it must never be reachable in a
    // production build. Config.buildEnvironment() is baked in at build time (VUE_APP_BUILD_ENVIRONMENT),
    // not user-controlled, so it's safe to use as the guard. The query param alone can no longer
    // flip this in production, fixing CodeQL js/user-controlled-bypass.
    const dummyModeAllowed = Config.buildEnvironment() !== PRODUCTION;
    return {
      step: steps.VALIDATION_CODE,
      company: queryParam("company") === "principal" ? "principal" : "cuprum",
      dummyMode: dummyModeAllowed && queryParam("dummyMode") === "true",
      jwtToken: undefined,
      validations: [],
      environment: Config.buildEnvironment(),
      config: undefined,
      allowedEnvironments: Config.allowedEnvironments(),
      authMethod: undefined,
      provider: undefined,
      clientRut: undefined,
      portalId: undefined,
      kongModeJWT: false,
      successResult: false,
      partialResult: true,
      validationCodeWithError: false,
      isPartialPortal: true,
      validationCode: queryParam("validationCode"),
      validationPurpose: undefined,
      jwt: queryParam("jwt")
    };
  },
  created() {
    this.config = new Config(this.environment);
    this.config.printInfo();
    if (this.validationCode) {
      this.clientDataValidator({ validationCode: this.validationCode });
    }
  },
  computed: {
    kongClient() {
      if (this.dummyMode) {
        return KongService.fromToken("devDummyModeToken");
      } else if (this.jwtToken) {
        return KongService.fromToken(this.jwtToken);
      } else {
        throw new Error("Missing parameter: either kong token or kong token credentials must be defined, found none");
      }
    },
    traceClient() {
      if (this.dummyMode) {
        return new DummyTraceService();
      } else {
        return new TraceService(this.kongClient, this.config.kafkaRestUrl(this.kongModeJWT), {
          traceId: this.traceId,
          channel: "auth-widget",
          company: this.company,
          operation: `auth-portal`,
          clientId: this.clientRut,
          advisorId: this.executiveRut
        });
      }
    },
    portalService() {
      if (this.dummyMode) {
        return new DummyPortalService();
      } else {
        return new PortalService(
          this.kongClient,
          this.traceClient,
          this.config.authApiUrl(this.kongModeJWT),
          this.config.authApiUrlPFG(this.kongModeJWT)
        );
      }
    }
  },
  methods: {
    compareValidationsByMethod(source, target) {
      if (source.method < target.method) return -1;
      else if (source.method > target.method) return 1;
      else return 0;
    },
    clientDataValidator(data) {
      let service = PortalService;
      if (this.dummyMode) {
        console.log(data);
        service = DummyPortalService;
      }
      this.step = steps.LOADING;
      try {
        service
          .checkValidationCode(
            this.config.authApiUrl(true),
            this.config.authApiUrlPFG(true),
            this.jwt,
            data.validationCode,
            this.company
          )
          .then(response => {
            this.portalId = response.portalId;
            this.clientRut = response.clientRut;
            this.validations = response.validations.sort(this.compareValidationsByMethod);
            this.jwtToken = response.jwt;
            this.kongModeJWT = true;
            this.step = steps.METHOD_SELECTOR;
            this.validationPurpose = response.validationPurpose
          })
          .catch(() => {
            this.validationCodeWithError = true;
            this.step = steps.VALIDATION_CODE;
          });
      } catch (error) {
        this.validationCodeWithError = true;
        this.step = steps.VALIDATION_CODE;
        console.error(`Call to check code failed. Error: ${error}`);
      }
    },
    beginAuthentication(response) {
      if (this.dummyMode) {
        console.log(response);
      }
      let selectedValidation = this.validations.find(x => x.method === response.selectedMethod);
      this.provider = selectedValidation.provider;
      this.authMethod = response.internalName;
      this.step = steps.AUTHENTICATION_PROCESS;
    },
    checkValidations() {
      this.portalService.checkValidationMethods(this.portalId, this.company).then(validationStatus => {
        if (validationStatus.status === "SUCCESS") {
          this.successResult = true;
          this.partialResult = false;
          this.step = steps.RESULT_SCREEN;
        } else {
          let tries = 0;
          validationStatus.validations.forEach(validation => {
            tries += validation.left;
          });
          if (tries > 0) {
            this.validations = validationStatus.validations.sort(this.compareValidationsByMethod);
            this.step = steps.METHOD_SELECTOR;
          } else {
            this.successResult = false;
            this.partialResult = false;
            this.step = steps.RESULT_SCREEN;
          }
        }
      });
    },
    checkAuthResponse(response) {
      if (this.dummyMode) {
        console.log(response);
      }
      this.step = steps.LOADING;
      if (response.status === "NOK") {
        this.successResult = false;
        this.partialResult = true;
        this.step = steps.RESULT_SCREEN;
      } else {
        this.checkValidations();
      }
    },
    returnMethodSelector() {
      this.step = steps.METHOD_SELECTOR;
    },
    showComponentValidationCode() {
      return this.step === steps.VALIDATION_CODE;
    },
    showComponentMethodSelector() {
      return this.step === steps.METHOD_SELECTOR;
    },
    showAuthWidget() {
      return this.step === steps.AUTHENTICATION_PROCESS;
    },
    showComponentResultScreen() {
      return this.step === steps.RESULT_SCREEN;
    },
    showComponentLoading() {
      return this.step === steps.LOADING;
    },
    checkTries() {
      this.step = steps.LOADING;
      this.checkValidations();
    }
  }
};
</script>
<style scoped src="@pds/core/core.min.css"></style>
<style scoped>
.auth-portal-container {
  height: 100vh;
  min-width: 375px;
  align-self: center;
  font-family: "FS Elliot Web Regular", sans-serif;
  margin: auto;
  display: flex;
  flex-direction: column;
}
.componentContainer {
  margin-bottom: auto;
  height: inherit;
}
.division-line-blue {
  left: 0%;
  right: 0%;
  width: 100%;
  bottom: 0%;
  height: 3px;
  background: #0076cf;
}
.division-line-orange {
  left: 0%;
  right: 0%;
  top: 0%;
  bottom: 96.74%;
  background: #faa443;
  height: 3px;
}

.header {
  min-height: 100px;
  display: flex;
  flex-direction: column;
}
.header-container {
  display: flex;
  justify-content: space-around;
}

.logo-container {
  padding: 20px 0px 21px 0px;
  width: max-content;
  float: left;
}
.contact-container {
  padding-top: 45px;
  width: max-content;
  float: right;
}
.phone-icon {
  height: 23px;
  width: 27px;
  float: left;
  padding-left: 30px;
}
.phone-contact {
  height: 20px;
  left: 35px;
  top: calc(50% - 20px / 2 - 0.5px);
  font-size: 16px;
  color: #4a4a4a;
  width: max-content;
  float: left;
  padding-left: 8px;
}
.footer {
  background: #f6f6f6;
  height: max-content;
  display: inline-block;
}
.footer-container {
  display: flex;
  justify-content: center;
}
.logo-sp {
  padding-top: 19px;
  float: left;
}
.footer-text {
  font-size: 14px;
  line-height: 18px;
  color: #4a4a4a;
  max-width: 800px;
  float: right;
  padding: 25px 50px;
}
.principal-copyright-text {
  font-size: 18px;
  line-height: 23px;
  color: #000000;
  float: left;
  padding: 30px 5px 30px 5px;
  font-weight: bold;
}
.principal-address-text {
  font-size: 18px;
  line-height: 23px;
  color: #000000;
  float: right;
  padding: 30px 5px 30px 5px;
  font-weight: bold;
}
.auth-widget-container {
  height: 100%;
  text-align: center;
  max-width: 850px;
  margin: auto;
}
.loader {
  text-align: center;
  margin: auto;
  padding-top: 100px;
  color: #4a4a4a;
}
/* Middle Screen Settings */
@media only screen and (min-width: 700px) and (max-width: 1150px) {
  .logo-sp {
    float: center;
    margin: auto;
    min-width: 90%;
    text-align: center;
  }
  .principal-copyright-text {
    text-align: center;
  }
  .principal-address-text {
    text-align: center;
    padding-top: 0px;
  }
  .auth-widget-container {
    padding-top: 20px;
    padding-bottom: 20px;
  }
  .footer-container {
    flex-direction: column;
    background: none;
    align-items: center;
  }
  .loader {
    padding-top: 80px;
  }
}
@media only screen and (max-width: 700px) {
  .contact-container {
    display: none;
  }
  .logo-container {
    width: 100%;
    text-align: center;
  }
  .logo-sp {
    float: center;
    margin: auto;
    min-width: 90%;
    text-align: center;
  }
  .footer-container {
    flex-direction: column;
    background: none;
  }
  .footer-text {
    font-size: 14px;
    line-height: 18px;
    text-align: center;
  }
  .principal-copyright-text {
    text-align: center;
  }
  .principal-address-text {
    text-align: center;
    padding-top: 0px;
  }
  .auth-widget-container {
    padding-top: 10px;
    padding-bottom: 10px;
  }
  .loader {
    padding-top: 40px;
  }
}
</style>
